import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Info from '@mui/icons-material/Info';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import CardGiftcard from '@mui/icons-material/CardGiftcard';
import ContentCut from '@mui/icons-material/ContentCut';
import Person from '@mui/icons-material/Person';
import { AnimatedTabs } from '../../../../components/ui';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import ItemIcon from '../../../../components/ItemIcon';
import WikiItemLink from '../../../../components/WikiItemLink';
import WikiInfoIcon from '../../../../components/WikiInfoIcon';
import type { NpcDetailDto } from '../../../../types';
import {
  BG_COLORS,
  BORDER_COLORS,
} from '../../../../utils/colors';
import { WIKI_DETAIL, SCROLL_RESERVE } from '../../../../utils/responsive';

interface NpcDetailProps {
  npc: NpcDetailDto;
}

// Helper functions for formatting
const formatNumber = (num?: number): string => {
  if (!num || num === 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatBoolean = (value: boolean | undefined, t: (key: string) => string): string => {
  return value ? t('booleanValues.yes') : t('booleanValues.no');
};

const getRaceColor = (race: string): string => {
  const raceColors: { [key: string]: string } = {
    'HUMANOID': '#4ecdc4',
    'UNDEAD': '#95a5a6',
    'BEAST': '#f39c12',
    'PLANT': '#27ae60',
    'BUG': '#e74c3c',
    'DRAGON': '#8e44ad',
    'ANGEL': '#f1c40f',
    'DEMON': '#c0392b',
    'GIANT': '#34495e',
    'ELEMENTAL': '#3498db'
  };
  return raceColors[race] || '#95a5a6';
};


const NpcDetail: React.FC<NpcDetailProps> = ({ npc }) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const scrollRef = useRef<any>(null);

  // Helper to check if value is valid (not empty, not "-", not "NONE")
  const isValidValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed !== '' && trimmed !== '-' && trimmed !== 'NONE';
    }
    return true;
  };

  // Calculate available tabs with icons for AnimatedTabs
  const availableTabs = useMemo(() => {
    const tabs: Array<{ value: string; label: string; icon: React.ReactElement }> = [
      { value: 'info', label: t('detailTabs.info'), icon: <Info /> }
    ];

    if (npc.skills && npc.skills.length > 0) {
      tabs.push({ value: 'skills', label: t('detailTabs.skills'), icon: <AutoAwesome /> });
    }

    if (npc.drops && npc.drops.length > 0) {
      tabs.push({ value: 'drop', label: t('detailTabs.drop'), icon: <CardGiftcard /> });
    }

    if (npc.stripping && npc.stripping.length > 0) {
      tabs.push({ value: 'stripping', label: t('detailTabs.stripping'), icon: <ContentCut /> });
    }

    return tabs;
  }, [npc, t]);

  const handleTabChange = (value: string) => {
    const idx = availableTabs.findIndex(t => t.value === value);
    setActiveTab(idx >= 0 ? idx : 0);
    // Reset scroll position
    if (scrollRef.current) {
      scrollRef.current.getScrollElement().scrollTop = 0;
    }
  };

  // Filter out ID and other unwanted fields, and empty values
  const getFilteredProperties = () => {
    const properties: Array<{ label: string; value: any; type?: string }> = [];

    // Basic Properties
    if (isValidValue(npc.race)) properties.push({ label: t('npcProperties.race'), value: npc.race });
    if (npc.lvl) properties.push({ label: t('npcProperties.level'), value: npc.lvl, type: 'stat' });

    // Combat Stats
    if (npc.hp && npc.hp > 0) properties.push({ label: 'HP', value: formatNumber(npc.hp), type: 'stat' });
    if (npc.mp && npc.mp > 0) properties.push({ label: 'MP', value: formatNumber(npc.mp), type: 'stat' });
    if (npc.physicalAttack && npc.physicalAttack > 0) properties.push({ label: t('npcProperties.physicalAttack'), value: formatNumber(npc.physicalAttack), type: 'stat' });
    if (npc.magicalAttack && npc.magicalAttack > 0) properties.push({ label: t('npcProperties.magicalAttack'), value: formatNumber(npc.magicalAttack), type: 'stat' });
    if (npc.physicalDefence && npc.physicalDefence > 0) properties.push({ label: t('npcProperties.physicalDefense'), value: formatNumber(npc.physicalDefence), type: 'stat' });
    if (npc.magicalDefence && npc.magicalDefence > 0) properties.push({ label: t('npcProperties.magicalDefense'), value: formatNumber(npc.magicalDefence), type: 'stat' });
    if (npc.evasion && npc.evasion > 0) properties.push({ label: t('npcProperties.evasion'), value: formatNumber(npc.evasion), type: 'stat' });

    // Regeneration
    if (npc.hpRegen && npc.hpRegen > 0) properties.push({ label: t('npcProperties.hpRegen'), value: npc.hpRegen.toFixed(2), type: 'stat' });
    if (npc.mpRegen && npc.mpRegen > 0) properties.push({ label: t('npcProperties.mpRegen'), value: npc.mpRegen.toFixed(2), type: 'stat' });

    // Experience & Points
    if (npc.exp && npc.exp > 0) properties.push({ label: t('npcProperties.exp'), value: formatNumber(npc.exp), type: 'reward' });
    if (npc.sp && npc.sp > 0) properties.push({ label: 'SP', value: formatNumber(npc.sp), type: 'reward' });
    if (npc.catacombPoint && npc.catacombPoint > 0) properties.push({ label: t('npcProperties.catacombs'), value: formatNumber(npc.catacombPoint), type: 'reward' });
    if (npc.vitalityPoint && npc.vitalityPoint > 0) properties.push({ label: t('npcProperties.necropolis'), value: formatNumber(npc.vitalityPoint), type: 'reward' });

    // Behavioral Properties
    if (npc.isAggressive !== undefined) properties.push({ label: t('npcProperties.aggressive'), value: formatBoolean(npc.isAggressive, t), type: 'behavior' });

    // Wiki Properties
    if (isValidValue(npc.wikiCategory)) properties.push({ label: t('npcProperties.category'), value: npc.wikiCategory });

    return properties;
  };

  const properties = getFilteredProperties();

  // Get current tab index based on tab value
  const getTabIndex = (tabValue: string): number => {
    const index = availableTabs.findIndex(tab => tab.value === tabValue);
    return index >= 0 ? index : 0;
  };

  return (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    }}>
        {/* Header Section - Fixed */}
        <Box sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 1.5, sm: 3 },
          mb: { xs: 1, sm: 3 }
        }}>
          <Box sx={{ flexShrink: 0 }}>
            {npc.wikiIcon ? (
              <ItemIcon
                icon={npc.wikiIcon}
                iconPanel=""
                name={npc.name}
                size={isSm ? 64 : 40}
              />
            ) : (
              <Avatar
                sx={{
                  width: isSm ? 64 : 40,
                  height: isSm ? 64 : 40,
                  bgcolor: getRaceColor(npc.race),
                  fontSize: isSm ? '1.5rem' : '1rem'
                }}
              >
                <Person fontSize="inherit" />
              </Avatar>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography component="h1" sx={{ fontWeight: 700, mb: 0.5, fontSize: WIKI_DETAIL.titleFontSize }}>
              {npc.name}
            </Typography>
          </Box>
        </Box>

        {/* Tabs - Fixed */}
        {availableTabs.length > 1 && (
          <Box sx={{ flexShrink: 0 }}>
            <AnimatedTabs
              value={availableTabs[activeTab]?.value || 'info'}
              onChange={handleTabChange}
              tabs={availableTabs}
            />
          </Box>
        )}

        {/* Scrollable Content */}
        <SimpleBar
          ref={scrollRef}
          style={{
            flex: 1,
            minHeight: 0,
            paddingRight: isSm ? SCROLL_RESERVE.sm : SCROLL_RESERVE.xs,
            paddingBottom: 16
          }}
          autoHide={false}
        >
          {/* Tab: Info */}
          {activeTab === getTabIndex('info') && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
          {properties.length > 0 && (
            <Box>
              {/* Stats Group */}
              {properties.filter(p => p.type === 'stat').length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                    {t('detailSections.combatStats')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {properties.filter(p => p.type === 'stat').map((prop, index) => (
                      <Chip
                        key={index}
                        label={`${prop.label}: ${prop.value}`}
                        size="small"
                        sx={{
                          backgroundColor: BG_COLORS.GLASS,
                          color: 'text.primary',
                          fontWeight: 500,
                          height: WIKI_DETAIL.chipHeight,
                          '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Rewards Group */}
              {properties.filter(p => p.type === 'reward').length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                    {t('detailSections.rewards')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {properties.filter(p => p.type === 'reward').map((prop, index) => (
                      <Chip
                        key={index}
                        label={`${prop.label}: ${prop.value}`}
                        size="small"
                        sx={{
                          backgroundColor: BG_COLORS.GLASS,
                          color: 'text.primary',
                          fontWeight: 500,
                          height: WIKI_DETAIL.chipHeight,
                          '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Basic Info Group */}
              {properties.filter(p => !p.type || p.type === 'behavior').length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                    {t('detailSections.basicInfo')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {properties.filter(p => !p.type || p.type === 'behavior').map((prop, index) => (
                      <Chip
                        key={index}
                        label={`${prop.label}: ${prop.value}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: BORDER_COLORS.LIGHTER,
                          color: 'text.primary',
                          height: WIKI_DETAIL.chipHeight,
                          '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          </Box>
          )}

        {/* Tab: Skills */}
        {activeTab === getTabIndex('skills') && npc.skills && npc.skills.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                sm: 'repeat(auto-fill, minmax(200px, 1fr))',
              },
              gap: { xs: 1, sm: 1.5 }
            }}>
              {npc.skills.map((skill, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    backgroundColor: BG_COLORS.GLASS_DARKER,
                    border: `1px solid ${BG_COLORS.GLASS}`,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: BG_COLORS.GLASS_HOVER
                    }
                  }}
                >
                  <WikiItemLink
                    item={{
                      id: skill.skill_id,
                      skill_id: skill.skill_id,
                    }}
                    style={{ textDecoration: 'none' }}
                  >
                    <WikiInfoIcon />
                  </WikiItemLink>

                  {skill.icon_skill && (
                    <ItemIcon
                      icon={skill.icon_skill}
                      iconPanel={skill.icon_panel || ""}
                      name={skill.name}
                      size={isSm ? 32 : 28}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                      {skill.name}
                    </Typography>
                    {skill.level && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_DETAIL.smallTextFontSize }}>
                        {t('detailSections.skillLevel')} {skill.level}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Tab: Drop */}
        {activeTab === getTabIndex('drop') && npc.drops && npc.drops.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                md: 'repeat(auto-fill, minmax(220px, 1fr))',
              },
              gap: { xs: 1, sm: 1.5 }
            }}>
              {npc.drops.map((drop, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    backgroundColor: BG_COLORS.GLASS_DARKER,
                    border: `1px solid ${BG_COLORS.GLASS}`,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: BG_COLORS.GLASS_HOVER
                    }
                  }}
                >
                  <WikiItemLink
                    item={{
                      id: drop.id,
                      wikiCategoryId: drop.wikiCategoryId,
                      wikiSubCategoryId: drop.wikiSubCategoryId,
                      wikiTypeId: drop.wikiTypeId,
                      wikiSubTypeId: drop.wikiSubTypeId,
                      itemType: drop.itemType,
                    }}
                    style={{ textDecoration: 'none' }}
                  >
                    <WikiInfoIcon />
                  </WikiItemLink>

                  <ItemIcon
                    icon={drop.icon || ''}
                    iconPanel={drop.iconPanel || ''}
                    name={drop.name || 'Unknown Item'}
                    size={isSm ? 32 : 28}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                      {drop.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={`${drop.chance}%`}
                        sx={{
                          backgroundColor: BG_COLORS.GLASS,
                          color: 'text.secondary',
                          fontSize: WIKI_DETAIL.miniChipFontSize,
                          height: WIKI_DETAIL.miniChipHeight
                        }}
                      />
                      <Chip
                        size="small"
                        label={drop.minCount === drop.maxCount ?
                          `x${drop.minCount}` :
                          `x${drop.minCount}-${drop.maxCount}`}
                        sx={{
                          backgroundColor: BG_COLORS.GLASS,
                          color: 'text.secondary',
                          fontSize: WIKI_DETAIL.miniChipFontSize,
                          height: WIKI_DETAIL.miniChipHeight
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Tab: Stripping */}
        {activeTab === getTabIndex('stripping') && npc.stripping && npc.stripping.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                md: 'repeat(auto-fill, minmax(220px, 1fr))',
              },
              gap: { xs: 1, sm: 1.5 }
            }}>
              {npc.stripping.map((strip, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    backgroundColor: BG_COLORS.GLASS_DARKER,
                    border: `1px solid ${BG_COLORS.GLASS}`,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: BG_COLORS.GLASS_HOVER
                    }
                  }}
                >
                  <WikiItemLink
                    item={{
                      id: strip.id,
                      wikiCategoryId: strip.wikiCategoryId,
                      wikiSubCategoryId: strip.wikiSubCategoryId,
                      wikiTypeId: strip.wikiTypeId,
                      wikiSubTypeId: strip.wikiSubTypeId,
                      itemType: strip.itemType,
                    }}
                    style={{ textDecoration: 'none' }}
                  >
                    <WikiInfoIcon />
                  </WikiItemLink>

                  <ItemIcon
                    icon={strip.icon || ''}
                    iconPanel={strip.iconPanel || ''}
                    name={strip.name || 'Unknown Item'}
                    size={isSm ? 32 : 28}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                      {strip.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={`${strip.chance}%`}
                        sx={{
                          backgroundColor: BG_COLORS.GLASS,
                          color: 'text.secondary',
                          fontSize: WIKI_DETAIL.miniChipFontSize,
                          height: WIKI_DETAIL.miniChipHeight
                        }}
                      />
                      <Chip
                        size="small"
                        label={strip.minCount === strip.maxCount ?
                          `x${strip.minCount}` :
                          `x${strip.minCount}-${strip.maxCount}`}
                        sx={{
                          backgroundColor: BG_COLORS.GLASS,
                          color: 'text.secondary',
                          fontSize: WIKI_DETAIL.miniChipFontSize,
                          height: WIKI_DETAIL.miniChipHeight
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        </SimpleBar>
    </Box>
  );
};

export default NpcDetail;
